import React from 'react'

import { ZariContext } from '_zari/context'
import { Modal, Table, Button, Row, Col } from '_zari/core'
import * as OrdenCompraService from 'services/ordenCompra.service'

const parseFecha = f => {
  if (!f) return null
  if (f instanceof Date) return f
  if (typeof f === 'number') return new Date(f)
  if (typeof f === 'string') {
    const t = Date.parse(f)
    if (!Number.isNaN(t)) return new Date(t)
    let m = f.match(/^(\d{2})[\/](\d{2})[\/](\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/)
    if (m) {
      const [, dd, mm, yyyy, HH = '00', MM = '00', SS = '00'] = m
      return new Date(+yyyy, +mm - 1, +dd, +HH, +MM, +SS)
    }
    m = f.match(/^(\d{2})[-](\d{2})[-](\d{4})$/)
    if (m) {
      const [, dd, mm, yyyy] = m
      return new Date(+yyyy, +mm - 1, +dd)
    }
    m = f.match(/^(\d{4})[\/](\d{2})[\/](\d{2})$/)
    if (m) {
      const [, yyyy, mm, dd] = m
      return new Date(+yyyy, +mm - 1, +dd)
    }
  }
  return null
}

const ListaOrdenesModal = ({
  mostrarRefrescar,
  show,
  handleClose,
  idSocio,
  values,
  setValues,
  dispatch,
  idsOCsYaSeleccionadas = [],
  registrarOrdenSeleccionada
}) => {
  const __zariContext = React.useContext(ZariContext)

  const [listaOrdenes, setListaOrdenes] = React.useState([])
  const [filtroSerieNumero, setFiltroSerieNumero] = React.useState('')

  React.useEffect(() => {
    OrdenCompraService.getOrdenes_ParaCompra({ idSocio: idSocio })
      .then(({ data }) => {
        const ordenadas = [...data].sort((a, b) => {
          // Ordenar por serie desc, luego número desc, luego fecha desc.
          const sa = `${a.serieDocumento || ''}`
          const sb = `${b.serieDocumento || ''}`
          if (sa !== sb) return sb.localeCompare(sa)
          const na = Number(a.numeroDocumento) || 0
          const nb = Number(b.numeroDocumento) || 0
          if (na !== nb) return nb - na
          const fa = parseFecha(a.fechaEmision) || new Date(0)
          const fb = parseFecha(b.fechaEmision) || new Date(0)
          return fb - fa
        })
        setListaOrdenes(ordenadas)
      })
      .catch(() => {
        __zariContext.snack('error', 'No se pudo obtener las ordenes de compra.')
      })
  }, [__zariContext, idSocio])

  const ordenesFiltradas = React.useMemo(() => {
    const disponibles = listaOrdenes.filter(row => !idsOCsYaSeleccionadas.includes(row._id))
    if (!filtroSerieNumero.trim()) return disponibles
    const texto = filtroSerieNumero.trim().toUpperCase()
    return disponibles.filter(row => {
      const serieNum = `${row.serieDocumento || ''} - ${row.numeroDocumento || ''}`.toUpperCase()
      return serieNum.includes(texto)
    })
  }, [listaOrdenes, filtroSerieNumero, idsOCsYaSeleccionadas])

  const clickSeleccionar = React.useCallback(
    ({ _id }) => {
      const idsPrevios = values._idsOrdenCompra || (values._idOrdenCompra ? [values._idOrdenCompra] : [])
      if (idsPrevios.includes(_id)) {
        __zariContext.snack('error', 'La orden de compra seleccionada ya fue agregada.')
        return
      }

      const infoOC = listaOrdenes.find(o => o._id === _id)
      if (infoOC && registrarOrdenSeleccionada) {
        registrarOrdenSeleccionada(_id, {
          serieDocumento: infoOC.serieDocumento,
          numeroDocumento: infoOC.numeroDocumento,
          fechaEmision: infoOC.fechaEmision,
          glosa: infoOC.glosa
        })
      }

      OrdenCompraService.getOrdenCompraOrden(_id).then(({ data }) => {
        const idOCResolved = data?._id || _id
        if (!idOCResolved) {
          __zariContext.snack('error', 'La orden de compra seleccionada no tiene identificador válido.')
          return
        }
        const nuevosIds = [...idsPrevios, idOCResolved]
        setValues({
          ...values,
          _idOrdenCompra: nuevosIds.length === 1 ? idOCResolved : values._idOrdenCompra,
          _idsOrdenCompra: nuevosIds
        })

        const detalles = (data?.detalles || []).map(({ _id: idDetalle, ...x }, k) => {
          return {
            ...x,
            _idDetalleOrden: idDetalle,
            _idOrdenCompraDetalle: idDetalle,
            _idOrdenCompra: idOCResolved,
            _idFila: `${idOCResolved}-${k}-row`,
            _fromOrdenCompra: true
          }
        })

        dispatch({ type: idsPrevios.length === 0 ? 'fill' : 'append', payload: detalles })

        mostrarRefrescar({
          importeGravado: data?.importeGravado,
          importeNoGravado: data?.importeNoGravado,
          importeOtros: data?.importeOtros,
          importeFlete: data?.importeFlete,
          importeIgv: data?.importeIgv,
          importeTotal: data?.importeTotal,
          montoPercepcion: data?.montoPercepcion,
          flagPercepcion: data?.flagPercepcion
        })

        handleClose()
      })
    },
    [setValues, values, handleClose, dispatch, mostrarRefrescar, __zariContext, listaOrdenes, registrarOrdenSeleccionada]
  )

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header>
        <Modal.Title>LISTA ORDENES</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='mb-2'>
          <Col md={6}>
            <input
              type='text'
              className='form-control form-control-sm'
              placeholder='Filtrar por serie-número...'
              value={filtroSerieNumero}
              onChange={e => setFiltroSerieNumero(e.target.value)}
            />
          </Col>
        </Row>
        <Table
          maxRowsShown={6}
          filas={ordenesFiltradas}
          headRows={[{ label: 'FECHA' }, { label: 'GLOSA' }, { label: 'SERIE-NUMERO' }, { label: '' }]}
          rowTemplate={row => (
            <Table.Row key={row._id}>
              <Table.Cell>{row.fechaEmision || '--'}</Table.Cell>
              <Table.Cell>{row.glosa || '--'}</Table.Cell>
              <Table.Cell>{`${row.serieDocumento} - ${row.numeroDocumento}`}</Table.Cell>
              <Table.Cell>
                <Button onClick={() => clickSeleccionar({ _id: row._id })}>Seleccionar</Button>
              </Table.Cell>
            </Table.Row>
          )}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button color='secondary' onClick={handleClose}>
          Cancelar
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ListaOrdenesModal
